import axios from 'axios';
import { updateTokens, logout } from '../store/slices/authSlice';
import { API_URL } from './constants';
import tabSessionService from '../services/tabSessionService';

const TAB_SESSION_BLOCKED = 'TAB_SESSION_BLOCKED';

const isAuthBootstrapRequest = (url = '') => {
  const path = String(url).replace(API_URL, '');
  const allowedPrefixes = [
    '/auth/login',
    '/auth/register',
    '/auth/refreshtoken',
    '/auth/me',
    '/auth/check-username',
    '/auth/check-email',
    '/auth/check-mobile',
    '/auth/check-organization',
    '/organizations/signup',
    '/otp/',
    '/email-otp/',
    '/password-reset',
    '/set-password',
  ];
  return allowedPrefixes.some((prefix) => path.startsWith(prefix));
};

/**
 * API client configuration.
 * Import API_URL, WS_URL, BACKEND_ORIGIN, CUMIN_API_URL from here or from ./constants.
 */
export * from './constants';

let refreshPromise = null;

const refreshAccessToken = async (refreshToken) => {
    if (refreshPromise) {
        return refreshPromise;
    }

    refreshPromise = axios.post(`${API_URL}/auth/refreshtoken`, {
        refreshToken,
    }).finally(() => {
        refreshPromise = null;
    });

    return refreshPromise;
};

// Setup interceptors
export const setupInterceptors = (store, options = {}) => {
    const { onTokenRefreshed } = options;
    // Request interceptor
    axios.interceptors.request.use(
        (config) => {
            if (
                !isAuthBootstrapRequest(config.url)
                && tabSessionService.shouldBlockRequests()
            ) {
                return Promise.reject(
                    Object.assign(new axios.Cancel(TAB_SESSION_BLOCKED), {
                        code: TAB_SESSION_BLOCKED,
                    }),
                );
            }

            const token = localStorage.getItem('token');
            if (token) {
                config.headers['Authorization'] = `Bearer ${token}`;
            }
            return config;
        },
        (error) => {
            return Promise.reject(error);
        }
    );

    // Response interceptor
    axios.interceptors.response.use(
        (response) => {
            return response;
        },
        async (error) => {
            const originalRequest = error.config;

            if (!originalRequest || originalRequest._retry) {
                return Promise.reject(error);
            }

            const canceled = error?.code === 'ERR_CANCELED'
                || error?.code === TAB_SESSION_BLOCKED
                || error?.message === TAB_SESSION_BLOCKED;
            if (canceled || !error.response) {
                return Promise.reject(error);
            }

            // 403 is authorization, not an expired session — don't force logout.
            if (error.response.status === 401) {
                originalRequest._retry = true;

                const refreshToken = localStorage.getItem('refreshToken');
                if (refreshToken) {
                    try {
                        console.log('🔄 Token expired/invalid, attempting silent refresh...');
                        const response = await refreshAccessToken(refreshToken);

                        const { accessToken, refreshToken: newRefreshToken } = response.data;
                        
                        console.log('✅ Token refreshed successfully!');
                        store.dispatch(updateTokens({ 
                            token: accessToken, 
                            refreshToken: newRefreshToken 
                        }));

                        onTokenRefreshed?.(accessToken);

                        originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
                        return axios(originalRequest);
                    } catch (refreshError) {
                        console.error('❌ Silent refresh failed:', refreshError);
                        const refreshNetworkFailure = !refreshError?.response
                            || refreshError?.code === 'ERR_NETWORK';
                        if (!refreshNetworkFailure) {
                            store.dispatch(logout());
                        }
                        return Promise.reject(refreshError);
                    }
                } else {
                    console.warn('⚠️ No refresh token found, logging out.');
                    store.dispatch(logout());
                }
            }

            return Promise.reject(error);
        }
    );
};

export default API_URL;